/**
 * Zod i18n Error Map
 *
 * Custom error map for Zod validation that uses i18next for translations.
 * This provides localized validation error messages across the application.
 */

import i18n from '@/config/i18n';
import { z } from 'zod';

/**
 * Custom Zod error map that integrates with i18next
 *
 * Note: Using type cast due to tsc -b issues with Zod's internal types in project references mode.
 * The types are correct at runtime and verified by tsc --noEmit.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const zodI18nErrorMap = ((issue: any, _ctx: any) => {
  const t = i18n.t;

  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      if (issue.received === 'undefined' || issue.received === 'null') {
        return { message: t('validation:field.required') as string };
      }
      return {
        message: t('validation:field.invalidType', { expected: issue.expected }) as string
      };

    case z.ZodIssueCode.invalid_format:
      // Handle email and URL validation (Zod v4 uses invalid_format for these)
      if (issue.type === 'email') {
        return { message: t('validation:email.invalid') as string };
      }
      if (issue.type === 'url') {
        return { message: t('validation:url.invalid') as string };
      }
      return { message: t('validation:field.invalid') as string };

    case z.ZodIssueCode.too_small:
      if (issue.type === 'string') {
        if (issue.minimum === 1) {
          return { message: t('validation:field.required') as string };
        }
        return {
          message: t('validation:string.minLength', { min: issue.minimum }) as string
        };
      }
      if (issue.type === 'number') {
        return {
          message: t('validation:number.min', { min: issue.minimum }) as string
        };
      }
      break;

    case z.ZodIssueCode.too_big:
      if (issue.type === 'string') {
        return {
          message: t('validation:string.maxLength', { max: issue.maximum }) as string
        };
      }
      if (issue.type === 'number') {
        return {
          message: t('validation:number.max', { max: issue.maximum }) as string
        };
      }
      break;

    case z.ZodIssueCode.custom:
      // Support custom i18n keys via params
      if (issue.params?.i18nKey) {
        return {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          message: t(issue.params.i18nKey as string, issue.params as any) as string
        };
      }
      break;
  }

  // Fallback to default error message
  return { message: t('validation:field.invalid') as string };
}) as z.ZodErrorMap;

/**
 * Set the custom error map as the default for Zod
 * This should be called once at application startup
 */
export const initializeZodI18n = () => {
  z.setErrorMap(zodI18nErrorMap);
};
